import { useEffect, useState } from "react";
import { Loader2, Printer, Tag } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DatePicker } from "@/components/ui/date-picker";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { generateLabelPdf, type LabelFormat, type LabelWeightUnit } from "@/lib/labelsApi";
import type { Product } from "@/lib/types";

interface LabelDialogProps {
  open: boolean;
  product: Pick<Product, "id" | "name" | "labelInfo"> | null;
  onClose: () => void;
}

const pad = (n: number) => String(n).padStart(2, "0");

/** Data de hoje no fuso de negocio (America/Sao_Paulo) como yyyy-MM-dd. */
function todayBR(): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Sao_Paulo",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
  return parts; // en-CA => yyyy-MM-dd
}

function addDays(isoDate: string, days: number): string {
  const [y, m, d] = isoDate.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + days);
  return `${dt.getUTCFullYear()}-${pad(dt.getUTCMonth() + 1)}-${pad(dt.getUTCDate())}`;
}

/** Lote padrao ddMMyyyy-01 a partir da data de fabricacao. */
function defaultLote(isoDate: string): string {
  const [y, m, d] = isoDate.split("-");
  return `${d}${m}${y}-01`;
}

export function LabelDialog({ open, product, onClose }: LabelDialogProps) {
  const [name, setName] = useState("");
  const [lote, setLote] = useState("");
  const [fabricadoEm, setFabricadoEm] = useState("");
  const [validoAte, setValidoAte] = useState("");
  const [netWeight, setNetWeight] = useState<string>("");
  const [weightUnit, setWeightUnit] = useState<LabelWeightUnit>("g");
  const [portion, setPortion] = useState<string>("");
  const [quantity, setQuantity] = useState(1);
  const [format, setFormat] = useState<LabelFormat>("FULL");
  const [submitting, setSubmitting] = useState(false);

  const compact = format === "COMPACT";

  useEffect(() => {
    if (!open || !product) return;
    const info = product.labelInfo;
    const today = todayBR();
    setName(product.name);
    setFabricadoEm(today);
    setLote(defaultLote(today));
    setValidoAte(info?.shelfLifeDays ? addDays(today, info.shelfLifeDays) : "");
    setNetWeight(info?.netWeightGrams != null ? String(info.netWeightGrams) : "");
    setWeightUnit("g");
    setPortion(info?.portionSizeGrams != null ? String(info.portionSizeGrams) : "");
    setQuantity(1);
    setFormat("FULL");
  }, [open, product]);

  // Recalcula lote quando o operador muda a data de fabricacao (mantendo o sequencial atual se editado).
  const onFabricadoChange = (value: string) => {
    setFabricadoEm(value);
    if (value) {
      setLote(defaultLote(value));
      const info = product?.labelInfo;
      if (info?.shelfLifeDays) setValidoAte(addDays(value, info.shelfLifeDays));
    }
  };

  const submit = async () => {
    if (!product) return;
    if (!validoAte) {
      toast.error("Informe a validade para gerar a etiqueta");
      return;
    }
    if (quantity < 1) {
      toast.error("Quantidade deve ser no mínimo 1");
      return;
    }
    setSubmitting(true);
    try {
      await generateLabelPdf({
        productId: product.id,
        nameOverride: name.trim() && name.trim() !== product.name ? name.trim() : undefined,
        lote: compact ? undefined : lote.trim() || undefined,
        fabricadoEm: fabricadoEm || undefined,
        validoAte: validoAte || undefined,
        netWeightGrams: compact || !netWeight ? undefined : Number(netWeight),
        netWeightUnit: weightUnit,
        portionSizeGrams: compact || !portion ? undefined : Number(portion),
        quantity,
        format,
      });
      toast.success("Etiqueta gerada");
      onClose();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Falha ao gerar etiqueta");
    } finally {
      setSubmitting(false);
    }
  };

  const info = product?.labelInfo;
  const noNutrition = info == null || info.energyKcal == null;
  const barcodeMissing = info?.hasBarcode && (!info.ean || info.ean.trim() === "");

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-md overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display text-xl">
            <Tag className="h-5 w-5 text-primary" /> Gerar etiqueta
          </DialogTitle>
        </DialogHeader>

        {product && (
          <div className="space-y-4 text-sm">
            <div className="space-y-1.5">
              <Label>Nome na etiqueta</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={product.name} />
              {name.trim() && name.trim() !== product.name && (
                <p className="text-xs text-muted-foreground">
                  Só altera o texto desta etiqueta — o cadastro do produto continua “{product.name}”.
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label>Tamanho da etiqueta</Label>
              <Select value={format} onValueChange={(v) => setFormat(v as LabelFormat)}>
                <SelectTrigger aria-label="Tamanho da etiqueta">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="FULL">Completa — 50 × 110 mm (com nutricional)</SelectItem>
                  <SelectItem value="COMPACT">Compacta — 50 × 30 mm (nome + datas)</SelectItem>
                </SelectContent>
              </Select>
              {compact && (
                <p className="text-xs text-muted-foreground">
                  Etiqueta menor, otimizada para bobinas 30×50. Sai só com o nome do produto, a data de
                  fabricação e a data de validade.
                </p>
              )}
            </div>

            {!compact && noNutrition && (
              <p className="rounded-lg border border-amber-300 bg-amber-50 p-2.5 text-xs text-amber-800">
                Este produto ainda não tem informação nutricional cadastrada. A etiqueta sairá com
                valores zerados — preencha na página do produto.
              </p>
            )}
            {!compact && barcodeMissing && (
              <p className="rounded-lg border border-amber-300 bg-amber-50 p-2.5 text-xs text-amber-800">
                Produto marcado com código de barras, mas sem número EAN. A etiqueta sairá sem o código.
              </p>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Fabricado em</Label>
                <DatePicker value={fabricadoEm} onChange={onFabricadoChange} />
              </div>
              <div className="space-y-1.5">
                <Label>Válido até</Label>
                <DatePicker value={validoAte} onChange={setValidoAte} />
                {!validoAte && (
                  <p className="text-xs text-destructive">Obrigatório para gerar a etiqueta.</p>
                )}
              </div>
            </div>

            {!compact && (
            <div className="space-y-1.5">
              <Label>Lote</Label>
              <Input value={lote} onChange={(e) => setLote(e.target.value)} placeholder="ddMMaaaa-01" />
            </div>
            )}

            {!compact && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Peso líquido</Label>
                <div className="flex items-center gap-1.5">
                  <Input
                    type="number"
                    min={0}
                    step="any"
                    value={netWeight}
                    onChange={(e) => setNetWeight(e.target.value)}
                    placeholder="ex.: 150"
                  />
                  <Select value={weightUnit} onValueChange={(v) => setWeightUnit(v as LabelWeightUnit)}>
                    <SelectTrigger className="w-20 shrink-0" aria-label="Unidade do peso">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="g">g</SelectItem>
                      <SelectItem value="kg">kg</SelectItem>
                      <SelectItem value="ml">ml</SelectItem>
                      <SelectItem value="un">un</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Porção ({weightUnit === "ml" ? "ml" : "g"})</Label>
                <Input
                  type="number"
                  min={0}
                  step="any"
                  value={portion}
                  onChange={(e) => setPortion(e.target.value)}
                  placeholder="ex.: 40"
                />
              </div>
            </div>
            )}

            <div className="space-y-1.5">
              <Label>Quantidade de etiquetas</Label>
              <Input
                type="number"
                min={1}
                value={quantity}
                onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value || "1", 10)))}
              />
              <p className="text-xs text-muted-foreground">
                As etiquetas saem em 2 colunas (2-up) no PDF, prontas para a Argox.
              </p>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Cancelar
          </Button>
          <Button variant="hero" onClick={() => void submit()} disabled={submitting || !product || !validoAte}>
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />}
            Gerar PDF
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
